<script setup>
/**
 * WHAT IT DOES, AND WHEN — the first screen, and the only one that is mostly reading.
 *
 * ── THE STARTER IS THE FIRST DECISION, AND IT IS MADE ON A CONVERSATION ───────────────
 * A shop owner cannot judge a taxonomy. Asked to choose between "Ask", "Choose" and
 * "Choose from your system" he is being asked to hold our data model in his head before he
 * has decided what he wants. He CAN judge a conversation. So the three starters play as the
 * chat his own customer would have (`StarterChat.vue`), in either language, and nothing is
 * created by looking — the draft changes only when he presses Use this.
 *
 * Offered only while the list is still empty. A saved task opens here to change its name or
 * when it starts, and a gallery of other people's tasks on that screen would be an invitation
 * to overwrite one.
 *
 * ── HOW IT STARTS IS THREE CARDS NOW, BECAUSE THERE IS A FIELD FOR IT (P-10.9) ─────────
 * This screen once drew one trigger box and said why there was no second arm: nothing on the
 * wire carried "started by a menu instead". The graph carries it now (`definition.start`,
 * `{ mode: 'assistant' | 'words' | 'menu', words? }`), so the owner chooses: the assistant
 * decides (the default and the old behaviour, and the only mode that needs the trigger box),
 * only certain words (matched by Waslo, never by a model), or only a menu button. The
 * trigger box is shown for the first alone; the others store `''`.
 *
 * ── AND WHAT IT SAYS FIRST, AND WHERE IT RUNS (P-2.7, P-10.26) ────────────────────────
 * "First thing to say" is sent word for word when the task starts; "This task can run on"
 * narrows the channels it may start on, and "Every channel" is `null`.
 *
 * ── AND NO COUNTER ON IT ──────────────────────────────────────────────────────────────
 * `59 / 500` was read by a blind reader as "12% done, keep writing" — on a box where one
 * sentence is the correct answer and 500 is a backstop nobody will reach. The limit is
 * still enforced; what is drawn instead is three real triggers he can press, which is the
 * thing a counter was pretending to be: a way of knowing whether what he typed is enough.
 *
 * ── WHAT THIS SCREEN NOW SAYS, BESIDES ASKING TWO QUESTIONS ───────────────────────────
 * The same reader made three findings that all land here, and one block answers all three:
 *
 *  • "None of the five step names is where I connect my calendar. Checking whether I'm
 *    free on Thursday is the whole point of booking." The rail promises a complete path
 *    and the hard part is inside one of its names. A sixth rail step would be a lie about
 *    the sequence — a calendar is not a stage of writing a task, it is a step inside one.
 *    So the outline says which screen holds it, in the same breath as what the screen is.
 *  • "Nothing reassures." The old editor's grey block said "It cannot skip a step or
 *    invent one", and he named that as the single thing he was most worried about with a
 *    robot talking to his customers. The redesign deleted it. It is back, and it is on the
 *    first screen rather than the fourth, because that is where the worry starts.
 *  • "Step 1 wastes the screen — two questions and a large empty area, which reads as
 *    'this will take five long screens'." It was measurably true: on a saved task with no
 *    starters offered, everything below the trigger note was blank to the fold.
 *
 * The outline is built from `screens`, which is `stepsFor(draft)` — the SAME list the rail
 * and the guard walk. A task in which nothing can go wrong has four screens, and this
 * block then describes four. It cannot promise a screen the sequence does not have.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

import BilingualBox from './BilingualBox.vue';
import DescribeTaskCard from './DescribeTaskCard.vue';
import StarterChat from './StarterChat.vue';
import WasloInput from '../WasloInput.vue';
import WasloTextArea from '../WasloTextArea.vue';
import { useWasloI18n } from '../../i18n.js';
import { STARTERS } from '../../helpers/task-starters.js';
import {
  MAX_OPENING,
  MAX_START_WORD,
  MAX_START_WORDS,
  MAX_TRIGGER,
  START_MODES,
} from '../../helpers/task-compose.js';
import { CHANNEL_NAME_KEY } from '../../helpers/task-channels.js';

const props = defineProps({
  title: { type: String, default: '' },
  trigger: { type: String, default: '' },
  /** True while the list is still empty, which is the only time a starter is offered. */
  offerStarters: { type: Boolean, default: false },
  /** `stepsFor(draft)` — the screens this task actually has, in order. */
  screens: { type: Array, default: () => [] },
  /** Problems this screen owns, already filtered. */
  problems: { type: Array, default: () => [] },
  /** P-2.7: `{ en, ar }`, the first thing to say when the task starts. */
  opening: { type: Object, default: () => ({ en: '', ar: '' }) },
  /** P-10.9: `{ mode, words }`. */
  start: {
    type: Object,
    default: () => ({ mode: 'assistant', words: [] }),
  },
  /** P-10.26: the channels it runs on, or `null` for every channel. */
  channels: { type: Array, default: null },
  /** P-10.26: the channels to offer (`channelChoices`), in order. */
  channelOptions: { type: Array, default: () => [] },
  /** P-10.9: the titles of the menus that start it, or `null` while not known. */
  menusStarting: { type: Array, default: null },
  /** The account's "Your customers write in", for the opening line's boxes. */
  languages: { type: String, default: 'both' },
  /** P-8.2: the account's channels, which a described task is drafted for. */
  accountChannels: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'update:title',
  'update:trigger',
  'update:opening',
  'update:start',
  'update:channels',
  'use-starter',
  'use-described',
]);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.JOB.${name}`;

/** Which starter is playing, or `''`. Opens in place; never an overlay. */
const playing = ref('');

const isArabic = computed(() => String(locale.value ?? '').startsWith('ar'));

const cards = computed(() =>
  STARTERS.map(starter => ({
    id: starter.id,
    icon: `i-lucide-${starter.icon}`,
    title: isArabic.value ? starter.titleAr : starter.titleEn,
    blurb: isArabic.value ? starter.blurbAr : starter.blurbEn,
  }))
);

const has = name => props.problems.some(problem => problem.problem === name);

/**
 * A box is only told off once it has been touched.
 *
 * An untouched new task is not wrong, it is empty, and opening the first screen of a
 * composer to two red sentences teaches an owner that this product starts by disagreeing
 * with him. What is missing is still said — once, at the bottom, where the way forward is.
 */
const touched = ref(new Set());
const touch = name => {
  touched.value = new Set([...touched.value, name]);
};
const shows = name => touched.value.has(name) || props[name].trim() !== '';

const titleMessage = computed(() => {
  if (!shows('title')) return '';
  if (has('no_title')) return t(key('TITLE_REQUIRED'));
  if (has('title_long')) return t(key('TITLE_LONG'));
  return '';
});

const triggerMessage = computed(() => {
  if (!shows('trigger')) return '';
  if (has('no_trigger')) return t(key('TRIGGER_REQUIRED'));
  if (has('trigger_long')) return t(key('TRIGGER_LONG'));
  return '';
});

const setTitle = value => {
  touch('title');
  emit('update:title', value);
};

// ── how it starts (P-10.9) ────────────────────────────────────────────────────────────
const MODE_KEY = { assistant: 'ASSISTANT', words: 'WORDS', menu: 'MENU' };
const modeCards = computed(() =>
  START_MODES.map(mode => ({
    id: mode,
    label: t(key(`START_${MODE_KEY[mode]}`)),
    description: t(key(`START_${MODE_KEY[mode]}_BODY`)),
  }))
);
const mode = computed(() => props.start?.mode ?? 'assistant');
const words = computed(() => props.start?.words ?? []);
const setMode = next =>
  emit('update:start', {
    mode: next,
    words: next === 'words' ? words.value : [],
  });

/** The word being typed, before it is added. */
const newWord = ref('');
const addWord = () => {
  const word = newWord.value.trim();
  newWord.value = '';
  if (word === '' || words.value.includes(word)) return;
  touch('words');
  emit('update:start', { mode: 'words', words: [...words.value, word] });
};
const removeWord = word =>
  emit('update:start', {
    mode: 'words',
    words: words.value.filter(w => w !== word),
  });
const wordsMessage = computed(() => {
  if (!touched.value.has('words') && words.value.length === 0) return '';
  if (has('no_start_words')) return t(key('WORDS_REQUIRED'));
  if (has('start_words_many')) return t(key('WORDS_MANY'));
  if (has('start_word_long')) return t(key('WORD_LONG'));
  return '';
});
/** The menus' own titles, joined the way the page's language joins a list. */
const menusLine = computed(() =>
  (props.menusStarting ?? []).length === 0
    ? ''
    : t(key('MENUS_STARTING'), {
        menus: props.menusStarting.join(locale.value === 'ar' ? '، ' : ', '),
      })
);

// ── the first thing to say (P-2.7) ────────────────────────────────────────────────────
const openingMessage = computed(() =>
  has('opening_long') ? t(key('OPENING_LONG')) : ''
);
const setOpening = (lang, value) =>
  emit('update:opening', { ...props.opening, [lang]: value });
const setOpeningPair = pair =>
  emit('update:opening', { en: pair.en ?? '', ar: pair.ar ?? '' });

// ── where it runs (P-10.26) ───────────────────────────────────────────────────────────
const everyChannel = computed(() => props.channels === null);
const channelOn = kind => everyChannel.value || props.channels.includes(kind);
/** Every channel is `null`; unticking it starts from every channel offered, all ticked. */
const setEvery = on =>
  emit('update:channels', on ? null : [...props.channelOptions]);
const setChannel = (kind, on) => {
  const current = everyChannel.value
    ? [...props.channelOptions]
    : [...props.channels];
  const next = on
    ? [...new Set([...current, kind])]
    : current.filter(k => k !== kind);
  emit(
    'update:channels',
    props.channelOptions.filter(k => next.includes(k))
  );
};
const channelsMessage = computed(() =>
  has('no_channels') ? t(key('CHANNELS_NONE')) : ''
);

const setTrigger = value => {
  touch('trigger');
  emit('update:trigger', value);
};

/**
 * The outline of the whole job, built from the screens this task really has.
 *
 * Same id table as `ComposeRail`, same `RAIL.*` labels — so the outline and the rail can
 * never call the same screen two things — with one sentence each from `JOB.MAP.*`. An id
 * the rail would not recognise is skipped rather than guessed at.
 */
const SCREEN_KEY = {
  job: 'JOB',
  steps: 'STEPS',
  whatif: 'WHATIF',
  arabic: 'ARABIC',
  check: 'CHECK',
};

const outline = computed(() =>
  props.screens
    .filter(screen => screen in SCREEN_KEY)
    .map((screen, index) => ({
      id: screen,
      number: index + 1,
      label: t(`WASLO.TASKS.COMPOSE.RAIL.${SCREEN_KEY[screen]}`),
      about: t(key(`MAP.${SCREEN_KEY[screen]}`)),
    }))
);

/**
 * Three real triggers, from three different shops, that fill the box when pressed.
 *
 * Whole sentences rather than fragments, because what is being taught is the SHAPE of a
 * good trigger — a moment described, not a list of words a customer might type — and a
 * fragment would teach the opposite. Pressing one is an ordinary edit: it goes through
 * `setTrigger`, so the box is marked touched and its validation behaves as if typed.
 */
const examples = computed(() =>
  ['EXAMPLE_1', 'EXAMPLE_2', 'EXAMPLE_3'].map(name => ({
    id: name,
    text: t(key(name)),
  }))
);
</script>

<template>
  <section class="flex flex-col gap-6" data-waslo-job-screen>
    <div class="flex flex-col gap-1">
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </h2>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('DESCRIPTION')) }}
      </p>
    </div>

    <WasloInput
      :model-value="title"
      :label="t(key('TITLE'))"
      :hint="t(key('TITLE_HINT'))"
      :placeholder="t(key('TITLE_PLACEHOLDER'))"
      :message="titleMessage"
      message-type="error"
      data-waslo-job-title
      @update:model-value="setTitle"
    />

    <div
      v-if="offerStarters"
      class="flex flex-col gap-3"
      data-waslo-job-starters
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('STARTERS')) }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('STARTERS_HINT')) }}</p>

      <div class="grid gap-3 md:grid-cols-3">
        <div
          v-for="card in cards"
          :key="card.id"
          class="flex flex-col gap-2 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
          :data-waslo-job-starter="card.id"
        >
          <span
            class="size-5 text-n-slate-11"
            :class="card.icon"
            aria-hidden="true"
          />
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            <bdi>{{ card.title }}</bdi>
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            <bdi>{{ card.blurb }}</bdi>
          </p>
          <div class="flex mt-auto pt-2">
            <Button
              :label="t('WASLO.TASKS.COMPOSE.STARTER.SEE_IT')"
              variant="faded"
              color="slate"
              size="sm"
              :data-waslo-job-starter-see="card.id"
              @click="playing = playing === card.id ? '' : card.id"
            />
          </div>
        </div>
      </div>

      <!-- Opens IN PLACE, under the card it belongs to. Nothing here teleports. -->
      <StarterChat
        v-if="playing"
        :starter-id="playing"
        @use="id => emit('use-starter', id)"
      />

      <!-- P-8.2: or her own words, drafted into a task she checks before anything is saved. -->
      <DescribeTaskCard
        :channels="accountChannels"
        @described="result => emit('use-described', result)"
      />
    </div>

    <div class="flex flex-col gap-3" data-waslo-job-start>
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('START')) }}
      </p>
      <div class="grid gap-3 md:grid-cols-3">
        <RadioCard
          v-for="card in modeCards"
          :id="`waslo-job-start-${card.id}`"
          :key="card.id"
          name="waslo-job-start"
          :label="card.label"
          :description="card.description"
          :is-active="mode === card.id"
          :data-waslo-job-start-mode="card.id"
          @select="setMode(card.id)"
        />
      </div>
    </div>

    <div
      v-if="mode === 'words'"
      class="flex flex-col gap-3"
      data-waslo-job-words
    >
      <div class="flex items-end gap-2">
        <div class="flex-1">
          <WasloInput
            v-model="newWord"
            :label="t(key('WORDS'))"
            :hint="t(key('WORDS_HINT'))"
            :placeholder="t(key('WORDS_PLACEHOLDER'))"
            :max-length="MAX_START_WORD"
            :message="wordsMessage"
            message-type="error"
            data-waslo-job-word-input
            @keydown.enter.prevent="addWord"
          />
        </div>
        <Button
          :label="t(key('WORDS_ADD'))"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="words.length >= MAX_START_WORDS"
          data-waslo-job-word-add
          @click="addWord"
        />
      </div>
      <div v-if="words.length" class="flex flex-wrap gap-2">
        <span
          v-for="word in words"
          :key="word"
          class="flex items-center gap-1 px-2 py-1 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12"
          data-waslo-job-word
        >
          <bdi>{{ word }}</bdi>
          <button
            type="button"
            class="text-n-slate-11 hover:text-n-slate-12"
            :aria-label="t(key('WORDS_REMOVE'), { word })"
            data-waslo-job-word-remove
            @click="removeWord(word)"
          >
            <span class="size-3 i-lucide-x" aria-hidden="true" />
          </button>
        </span>
      </div>
    </div>

    <div
      v-if="mode === 'menu'"
      class="flex flex-col gap-1"
      data-waslo-job-menus
    >
      <p v-if="menusLine" class="mb-0 text-sm text-n-slate-12">
        <bdi>{{ menusLine }}</bdi>
      </p>
      <p
        v-else-if="menusStarting !== null"
        class="mb-0 text-sm text-n-amber-11"
        data-waslo-job-no-menu
      >
        {{ t(key('NO_MENU')) }}
      </p>
    </div>

    <div v-if="mode === 'assistant'" class="flex flex-col gap-3">
      <WasloTextArea
        :model-value="trigger"
        :label="t(key('TRIGGER'))"
        :hint="t(key('TRIGGER_HINT'))"
        :placeholder="t(key('TRIGGER_PLACEHOLDER'))"
        :message="triggerMessage"
        message-type="error"
        :max-length="MAX_TRIGGER"
        :show-count="false"
        min-height="4rem"
        data-waslo-job-trigger
        @update:model-value="setTrigger"
      />
      <!-- What the counter was pretending to be: a way of knowing this is enough. -->
      <p class="mb-0 text-sm text-n-slate-11" data-waslo-job-enough>
        {{ t(key('ENOUGH')) }}
      </p>
      <div class="flex flex-col gap-2" data-waslo-job-examples>
        <p class="mb-0 text-sm text-n-slate-11">{{ t(key('EXAMPLES')) }}</p>
        <div class="flex flex-col items-start gap-2">
          <button
            v-for="example in examples"
            :key="example.id"
            type="button"
            class="px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak text-start text-n-slate-12 bg-n-solid-1 hover:bg-n-alpha-2"
            :data-waslo-job-example="example.id"
            @click="setTrigger(example.text)"
          >
            <bdi>{{ example.text }}</bdi>
          </button>
        </div>
      </div>
    </div>

    <p
      v-if="mode === 'assistant'"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-job-trigger-note
    >
      {{ t(key('TRIGGER_NOTE')) }}
    </p>

    <!-- P-2.7: sent word for word when the task starts, in the customer's language. -->
    <div class="flex flex-col gap-1" data-waslo-job-opening>
      <BilingualBox
        :en="opening.en ?? ''"
        :ar="opening.ar ?? ''"
        :label="t(key('OPENING'))"
        :hint="t(key('OPENING_HINT'))"
        :max-length="MAX_OPENING"
        :message="openingMessage"
        multiline
        kind="task_message"
        :languages="languages"
        @update:en="value => setOpening('en', value)"
        @update:ar="value => setOpening('ar', value)"
        @update:pair="setOpeningPair"
      />
    </div>

    <!-- P-10.26: one task, a channel filter. Every channel is the default. -->
    <div
      v-if="channelOptions.length"
      class="flex flex-col gap-2"
      data-waslo-job-channels
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('CHANNELS')) }}
      </p>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Checkbox
          :model-value="everyChannel"
          data-waslo-job-channel-every
          @update:model-value="setEvery"
        />
        <span>{{ t(key('CHANNELS_EVERY')) }}</span>
      </label>
      <div class="flex flex-wrap gap-x-4 gap-y-2">
        <label
          v-for="kind in channelOptions"
          :key="kind"
          class="flex items-center gap-2 text-sm text-n-slate-12"
          :data-waslo-job-channel="kind"
        >
          <Checkbox
            :model-value="channelOn(kind)"
            @update:model-value="on => setChannel(kind, on)"
          />
          <span>{{ t(CHANNEL_NAME_KEY[kind]) }}</span>
        </label>
      </div>
      <p v-if="channelsMessage" class="mb-0 text-sm text-n-ruby-9" role="alert">
        {{ channelsMessage }}
      </p>
    </div>

    <!--
      The map of the job, and the promise. Last on the screen because it is read, not
      filled in — and it is exactly what was under the fold when this screen was two
      questions and a blank half-page.
    -->
    <div
      v-if="outline.length"
      class="flex flex-col gap-3 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-job-map
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('MAP_HEADING')) }}
      </p>
      <ol class="flex flex-col gap-2 mb-0">
        <li
          v-for="entry in outline"
          :key="entry.id"
          class="flex items-start gap-3"
          :data-waslo-job-map-screen="entry.id"
        >
          <span
            class="grid text-xs font-medium rounded-full shrink-0 place-items-center size-6 bg-n-alpha-2 text-n-slate-11"
            aria-hidden="true"
          >
            {{ entry.number }}
          </span>
          <span class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ entry.label }}
            </span>
            <span class="text-sm leading-6 text-n-slate-11">
              {{ entry.about }}
            </span>
          </span>
        </li>
      </ol>
      <p
        class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
        data-waslo-job-promise
      >
        {{ t(key('PROMISE')) }}
      </p>
    </div>
  </section>
</template>
